import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import '../styles/CatchupPage.css';
import { Link } from 'react-router-dom';

// Helper functions
const createSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/-+/g, '-')      // Replace multiple hyphens with single
    .trim();
};

const parseDateFromTitle = (title) => {
  const dateRegex = /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/;
  const match = title?.match?.(dateRegex);

  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);
    const fullYear = year < 100 ? 2000 + year : year;
    return new Date(fullYear, month, day);
  }
  return new Date(1970, 0, 1);
};

const sortEpisodesByDate = (episodes) => {
  return [...episodes].sort((a, b) => {
    const dateA = parseDateFromTitle(a.title);
    const dateB = parseDateFromTitle(b.title);
    return dateB - dateA;
  });
};

const formatTime = (seconds) => {
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

// Share functionality
const shareEpisode = async (showTitle, episodeTitle, showSlug, episodeSlug) => {
  const shareUrl = `${window.location.origin}/catchup/${showSlug}/${episodeSlug}`;
  const shareText = `Check out "${episodeTitle}" from ${showTitle} on Utter Radio!`;
  
  if (navigator.share) {
    try {
      await navigator.share({
        title: `${episodeTitle} - ${showTitle}`,
        text: shareText,
        url: shareUrl,
      });
    } catch (err) {
      console.log('Share failed:', err);
    }
  } else {
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      alert('Link copied to clipboard!');
    } catch (err) {
      // Final fallback: prompt manually
      prompt('Copy this link:', shareUrl);
    }
  }
};

export const CatchupPage = () => {
  const [shows, setShows] = useState([]);
  const [currentShowIndex, setCurrentShowIndex] = useState(0);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // single source of truth for the large player image
  const [playerImage, setPlayerImage] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { showSlug, episodeSlug } = useParams();
  
  const {
    isPlaying: globalIsPlaying,
    isCatchupMode,
    catchupCurrentTime,
    catchupDuration,
    loadCatchupEpisode,
    playCatchup,
    skipForward,
    skipBackward,
    togglePlayback,
    setCurrentStation,
    trackInfo,
  } = useAudioPlayer();

  const CONFIG = {
    dataSource: '/get_shows.php',
    defaultImage:
      '/images/UtterRadioLogoFull.jpg'
  };

  // Determine an origin to resolve relative image paths against (useful in dev)
  const dataSourceOrigin = (() => {
    try {
      const parsed = new URL(CONFIG.dataSource, window.location.origin);
      return parsed.origin;
    } catch (err) {
      return window.location.origin;
    }
  })();

  // Normalize image to an absolute URL where possible
  const normalizeImage = (url) => {
    if (!url) return CONFIG.defaultImage;
    if (/^(data:|blob:)/i.test(url)) return url;
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/')) return `${dataSourceOrigin}${url}`;
    try {
      return new URL(url, `${dataSourceOrigin}/`).href;
    } catch (err) {
      console.warn('normalizeImage failed for', url, err);
      return CONFIG.defaultImage;
    }
  };

  // Ref to track the last explicit image we set for playback (helps avoid accidental overrides)
  const lastExplicitPlayerImageRef = useRef(null);

  // Preload helper: only set playerImage after image successfully loads (prevents blank)
  const preloadAndSetImage = useCallback((url) => {
    const resolvedUrl = normalizeImage(url);
    if (!resolvedUrl) {
      setPlayerImage(CONFIG.defaultImage);
      lastExplicitPlayerImageRef.current = CONFIG.defaultImage;
      console.debug('[Catchup] preloadAndSetImage: no url, set default');
      return;
    }

    // If already the same value, avoid reloading
    if (resolvedUrl === lastExplicitPlayerImageRef.current) {
      console.debug('[Catchup] preloadAndSetImage: already current ->', resolvedUrl);
      return;
    }

    const img = new Image();
    let settled = false;

    img.onload = () => {
      if (settled) return;
      settled = true;
      setPlayerImage(resolvedUrl);
      lastExplicitPlayerImageRef.current = resolvedUrl;
      console.debug('[Catchup] preloaded and set playerImage:', resolvedUrl);
    };

    img.onerror = (err) => {
      if (settled) return;
      settled = true;
      console.warn('[Catchup] preload failed for', resolvedUrl, err || '');
      // fallback to default (but avoid repetitive state updates)
      if (lastExplicitPlayerImageRef.current !== CONFIG.defaultImage) {
        setPlayerImage(CONFIG.defaultImage);
        lastExplicitPlayerImageRef.current = CONFIG.defaultImage;
      }
    };

    // Start load (placing src after handlers to capture events)
    img.src = resolvedUrl;

    // In case browser has cached and onload doesn't fire synchronously, set a small timeout to guard
    setTimeout(() => {
      if (!settled && img.complete && img.naturalWidth) {
        settled = true;
        setPlayerImage(resolvedUrl);
        lastExplicitPlayerImageRef.current = resolvedUrl;
        console.debug('[Catchup] preload immediate-complete set playerImage:', resolvedUrl);
      }
    }, 50);
  }, [dataSourceOrigin]); // normalizeImage depends on dataSourceOrigin

  // Load shows & normalize images
  const loadShowsData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(CONFIG.dataSource);
      if (!response.ok) throw new Error('Failed to fetch shows');

      const data = await response.json();
      let showsData = Array.isArray(data?.shows) ? data.shows : [];

      if (!Array.isArray(showsData) || showsData.length === 0) {
        showsData = [
          {
            title: "Can't Load Shows",
            description: 'No Shows Found',
            image: '',
            episodes: [
              {
                title: 'N/A',
                description: 'Please Contact Support!',
                audio: '',
                duration: '0:00',
                image: ''
              }
            ]
          }
        ];
      }

      const normalizedShows = showsData.map((show) => {
        const normalizedShowImage = normalizeImage(show.image);
        const showSlug = createSlug(show.title);
        
        const normalizedEpisodes = (show.episodes || []).map((ep) => ({
          ...ep,
          image: normalizeImage(ep.image),
          slug: createSlug(ep.title),
          showSlug: showSlug
        }));

        return {
          ...show,
          slug: showSlug,
          image: normalizedShowImage,
          episodes: normalizedEpisodes
        };
      });

      setShows(normalizedShows);

      // Handle URL-based navigation (from params)
      if (showSlug && normalizedShows.length > 0) {
        const foundShowIndex = normalizedShows.findIndex(s => s.slug === showSlug);
        if (foundShowIndex >= 0) {
          setCurrentShowIndex(foundShowIndex);
          
          if (episodeSlug) {
            const episodes = normalizedShows[foundShowIndex].episodes;
            const foundEpisodeIndex = episodes.findIndex(ep => ep.slug === episodeSlug);
            if (foundEpisodeIndex >= 0) {
              setCurrentEpisodeIndex(foundEpisodeIndex);
              setTimeout(() => loadEpisode(foundShowIndex, foundEpisodeIndex), 100);
            }
          }
          
          // Set initial player image
          const initialShow = normalizedShows[foundShowIndex];
          const initialImg = initialShow?.image || CONFIG.defaultImage;
          preloadAndSetImage(initialImg);
          lastExplicitPlayerImageRef.current = initialImg;
          return; // Exit early if we handled URL params
        }
      }

      // initial selection from query params (backward compatibility)
      const params = new URLSearchParams(location.search);
      const showParam = params.get('show');
      const episodeParam = params.get('episode');
      const autoplay = params.get('autoplay') === 'true';

      let targetShowIndex = 0;
      let targetEpisodeIndex = -1;

      if (showParam && normalizedShows.length > 0) {
        const foundIndex = normalizedShows.findIndex((s) =>
          (s.title || '').toLowerCase().includes(showParam.toLowerCase()) ||
          showParam.toLowerCase().includes((s.title || '').toLowerCase())
        );
        if (foundIndex >= 0) targetShowIndex = foundIndex;

        if (episodeParam && normalizedShows[targetShowIndex]?.episodes) {
          const episodes = normalizedShows[targetShowIndex].episodes;
          const foundEpisodeIndex = episodes.findIndex((ep) =>
            (ep.title || '').toLowerCase().includes(episodeParam.toLowerCase())
          );
          if (foundEpisodeIndex >= 0) {
            targetEpisodeIndex = foundEpisodeIndex;
            if (autoplay) setTimeout(() => loadEpisode(targetShowIndex, targetEpisodeIndex), 100);
          }
        }
      }

      setCurrentShowIndex(targetShowIndex);

      // Explicitly set the initial player image
      const initialShow = normalizedShows[targetShowIndex];
      const initialImg =
        initialShow?.episodes?.[0]?.image || initialShow?.image || CONFIG.defaultImage;
      preloadAndSetImage(initialImg);
      lastExplicitPlayerImageRef.current = initialImg;
    } catch (err) {
      console.error('Error loading shows:', err);
      setError('Failed to load shows. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [location.search, preloadAndSetImage, showSlug, episodeSlug]);

  // Replace your existing loadEpisode function with this updated version
  const loadEpisode = (showIndex, episodeIndex) => {
    if (!shows[showIndex] || !shows[showIndex].episodes?.[episodeIndex]) return;

    const show = shows[showIndex];
    const episode = show.episodes[episodeIndex];

    setCurrentShowIndex(showIndex);
    setCurrentEpisodeIndex(episodeIndex);
    
    // Update URL to reflect current show/episode
    navigate(`/catchup/${show.slug}/${episode.slug}`, { replace: true });
    
    if (show.folder && episode.folder) {
        trackPlay(show.folder, episode.folder);
    }

    // USE THE SHOW IMAGE DIRECTLY if episode image is the default
    let chosenImage;
    if (episode.image && episode.image !== CONFIG.defaultImage) {
        chosenImage = episode.image;
    } else if (show.image && show.image !== CONFIG.defaultImage) {
        chosenImage = show.image;
    } else {
        chosenImage = CONFIG.defaultImage;
    }
    
    preloadAndSetImage(chosenImage);

    if (episode.audio) {
        loadCatchupEpisode(
            episode.audio,
            episode.title,
            show.title,
            chosenImage,
            () => nextEpisode()
        );

        setCurrentStation({
            name: show.title,
            logo: chosenImage
        });

        setTimeout(() => playCatchup(), 100);
    }
  };

  // when user clicks a show in the sidebar, set playerImage to the show's image (explicit)
  const handleSelectShow = (index) => {
    setCurrentShowIndex(index);
    setCurrentEpisodeIndex(-1);
    const show = shows[index];
    const img = show?.image || CONFIG.defaultImage;
    preloadAndSetImage(img);
    lastExplicitPlayerImageRef.current = img;
    
    // Update URL to show-level
    navigate(`/catchup/${show.slug}`, { replace: true });
  };

  // prev/next with guards
  const prevEpisode = () => {
    if (!shows[currentShowIndex]?.episodes) return;
    if (currentEpisodeIndex < shows[currentShowIndex].episodes.length - 1) {
      loadEpisode(currentShowIndex, currentEpisodeIndex + 1);
    } else if (currentShowIndex > 0) {
      loadEpisode(currentShowIndex - 1, 0);
    }
  };

  const nextEpisode = () => {
    if (!shows[currentShowIndex]?.episodes) return;
    if (currentEpisodeIndex > 0) {
      loadEpisode(currentShowIndex, currentEpisodeIndex - 1);
    } else if (currentShowIndex < shows.length - 1) {
      loadEpisode(currentShowIndex + 1, 0);
    }
  };

  useEffect(() => {
    loadShowsData();
  }, [loadShowsData]);

  // If shows arrive but playerImage somehow remained null/empty (first-load race), set it explicitly once.
  useEffect(() => {
    if ((!playerImage || playerImage === '') && shows.length > 0) {
      const s = shows[0];
      const fallback = s?.episodes?.[0]?.image || s?.image || CONFIG.defaultImage;
      preloadAndSetImage(fallback);
      lastExplicitPlayerImageRef.current = fallback;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shows]);

  // Debugging: log when global player state changes
  useEffect(() => {
    console.debug(
      '[Catchup] globalIsPlaying:',
      globalIsPlaying,
      'isCatchupMode:',
      isCatchupMode,
      'currentEpisodeIndex:',
      currentEpisodeIndex
    );
  }, [globalIsPlaying, isCatchupMode, currentEpisodeIndex]);

  // Debugging: log playerImage changes
  useEffect(() => {
    console.debug('[Catchup] playerImage changed ->', playerImage);
  }, [playerImage]);

  // NEW: respect trackInfo artwork only as a fallback when no episode is explicitly selected
  useEffect(() => {
    const incoming = trackInfo?.image;
    if (!incoming) return;

    const normalized = normalizeImage(incoming);

    // ignore invalid/falsy/default images or if it matches the last explicit image
    if (!normalized || normalized === CONFIG.defaultImage) {
      console.debug('[Catchup] trackInfo image ignored (invalid/default):', normalized);
      return;
    }
    if (normalized === lastExplicitPlayerImageRef.current) {
      console.debug('[Catchup] trackInfo image same as last explicit, ignoring:', normalized);
      return;
    }

    // Only accept track-supplied artwork when no episode is actively selected,
    // otherwise we risk overwriting an explicit episode image when playback starts.
    if (currentEpisodeIndex === -1) {
      console.debug('[Catchup] using trackInfo image as fallback:', normalized);
      preloadAndSetImage(normalized);
    } else {
      console.debug('[Catchup] trackInfo image ignored because an episode is selected:', normalized);
    }
  }, [trackInfo?.image, currentEpisodeIndex, preloadAndSetImage]);

  // current references used for render
  const currentShow = shows[currentShowIndex] || {};
  const currentEpisode = currentShow.episodes?.[currentEpisodeIndex] || {};
  const sortedEpisodes = currentShow.episodes ? sortEpisodesByDate(currentShow.episodes) : [];

  // Add this inside the CatchupPage component, near the top with other state/hooks
  const trackPlay = useCallback(async (showFolder, episodeFolder) => {
      try {
          await fetch('/admin/catchup/track_play.php', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                  show_folder: showFolder,
                  episode_folder: episodeFolder
              })
          });
          console.debug('[Catchup] Play tracked:', showFolder, episodeFolder);
      } catch (err) {
          console.error('[Catchup] Failed to track play:', err);
      }
  }, []);

  return (
    <>
      <div className="pageContainer">
        <section className="news-page-header">
          <h1>
            Catchup
          </h1>
          <p>Missed your favorite show? Listen to all our previous episodes here!</p>
        </section>

        <section className="catchup-player-container">
          <div className="catchup-header">
            <h2>
              Select Show
            </h2>
          </div>

          <div className="player-layout">
            <div className="player-main">
              <div className="now-playing">
                <img
                  id="current-show-image"
                  key={`${currentShowIndex}-${currentEpisodeIndex}`}
                  className="show-image"
                  src={currentShow.image || CONFIG.defaultImage}
                  alt="Show Image"
                  onLoad={(e) => {
                    console.debug('[Catchup] show-image onLoad:', e.currentTarget.src);
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    console.warn(
                      '[Catchup] Player image load failed, falling back to default:',
                      e.currentTarget.src
                    );
                    if (e.currentTarget.src !== CONFIG.defaultImage) {
                      setPlayerImage(CONFIG.defaultImage);
                      lastExplicitPlayerImageRef.current = CONFIG.defaultImage;
                    }
                    e.currentTarget.src = CONFIG.defaultImage;
                  }}
                />
                <h3 id="current-show-title" className="show-title-catchup">
                  {isLoading ? 'Loading shows...' : currentShow.title}
                </h3>
                <h4 id="current-episode-title" className="episode-title">
                  {currentEpisodeIndex >= 0 ? currentEpisode.title : 'Select an episode to play'}
                </h4>
                <p id="current-show-description" className="show-description">
                  {currentEpisodeIndex >= 0
                    ? currentEpisode.description || currentShow.description || 'No description available'
                    : 'Select an episode to play'}
                </p>
                
                {/* Share button - only show when an episode is selected */}
                {currentEpisodeIndex >= 0 && currentShow && currentEpisode && (
                  <button 
                    className="share-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      shareEpisode(
                        currentShow.title, 
                        currentEpisode.title,
                        currentShow.slug,
                        currentEpisode.slug
                      );
                    }}
                    title="Share this episode"
                  >
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                    Share Episode
                  </button>
                )}
              </div>
            </div>

            <div className="shows-sidebar">
              <div className="shows-header">
                <span>Shows</span>
              </div>
              <div className="shows-list" id="shows-list">
                {isLoading ? (
                  <div className="loading">
                    <div className="spinner" />
                    <p>Scanning shows folder...</p>
                  </div>
                ) : error ? (
                  <div className="no-content">
                    <i className="fas fa-exclamation-triangle" />
                    <p>{error}</p>
                  </div>
                ) : shows.length === 0 ? (
                  <div className="no-content">
                    <i className="fas fa-headphones-slash" />
                    <p>No shows found. Add shows to the shows folder.</p>
                  </div>
                ) : (
                  shows.map((show, index) => (
                    <div
                      key={index}
                      className={`show-item ${index === currentShowIndex ? 'active' : ''}`}
                      onClick={() => handleSelectShow(index)}
                    >
                      <img
                        src={show.image || CONFIG.defaultImage}
                        alt={show.title}
                        className="show-item-img"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = CONFIG.defaultImage;
                        }}
                      />
                      <div className="show-item-info">
                        <div className="show-item-title">{show.title}</div>
                        <div className="show-item-desc">{show.description || 'No description'}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="episodes-container">
            <div className="episodes-header">
              <span>
                Episodes in <span id="selected-show-name">{currentShow.title || 'Select a show'}</span>
              </span>
              <span className="episode-count" id="episode-count">
                {currentShow.episodes
                  ? `${currentShow.episodes.length} episode${currentShow.episodes.length !== 1 ? 's' : ''}`
                  : '0 episodes'}
              </span>
            </div>
            <div className="episodes-list" id="episodes-list">
              {!currentShow.episodes || currentShow.episodes.length === 0 ? (
                <div className="no-content">
                  <i className="fas fa-headphones" />
                  <p>Select a show to view its episodes</p>
                </div>
              ) : (
                sortedEpisodes.map((episode, index) => {
                  const originalIndex = currentShow.episodes.indexOf(episode);
                  const isActive = originalIndex === currentEpisodeIndex;
                  const isPlayingThis = isActive && globalIsPlaying && isCatchupMode;

                  return (
                    <div
                      key={index}
                      className={`episode-item ${isActive ? 'active' : ''} ${isPlayingThis ? 'playing' : ''}`}
                    >
                      <div 
                        className="episode-item-main"
                        onClick={() => loadEpisode(currentShowIndex, originalIndex)}
                      >
                        <div className="episode-item-play">
                          <svg
                            className="control-svg"
                            fill="#BD003A"
                            height="36px"
                            width="36px"
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 45.9 45.9"
                          >
                            <g
                              className="PlayMain"
                              transform="translate(0, 0) scale(0.08)"
                              style={{ display: isPlayingThis ? 'none' : 'inline' }}
                            >
                              <g className="playGroup">
                                <circle cx="250" cy="250" r="200" fill="white" />
                                <path
                                  className="cls-play"
                                  d="M229.5,0C102.751,0,0,102.751,0,229.5S102.751,459,229.5,459S459,356.249,459,229.5S356.249,0,229.5,0z M310.292,239.651 
                                    l-111.764,76.084c-3.761,2.56-8.63,2.831-12.652,0.704c-4.022-2.128-6.538-6.305-6.538-10.855V153.416 
                                    c0-4.55,2.516-8.727,6.538-10.855c4.022-2.127,8.891-1.857,12.652,0.704l111.764,76.084c3.359,2.287,5.37,6.087,5.37,10.151 
                                    C315.662,233.564,313.652,237.364,310.292,239.651z"
                                  fill="#BD003A"
                                />
                              </g>
                            </g>

                            <g
                              className="PauseMain"
                              transform="translate(0, 0) scale(0.072)"
                              style={{ display: isPlayingThis ? 'inline' : 'none' }}
                            >
                              <g className="pauseGroup">
                                <circle cx="250" cy="250" r="200" fill="white" />
                                <path
                                  className="cls-pause"
                                  d="M256,0C114.617,0,0,114.617,0,256s114.617,256,256,256s256-114.617,256-256S397.383,0,256,0z M336,320 c0,8.836-7.156,16-16,16H192c-8.844,0-16-7.164-16-16V192c0-8.836,7.156-16,16-16h128c8.844,0,16,7.164,16,16V320z"
                                  fill="#BD003A"
                                />
                              </g>
                            </g>
                          </svg>
                        </div>

                        <div className="episode-item-info">
                          <div className="episode-item-title">{episode.title}</div>
                          <div className="episode-item-desc">{episode.description || 'No description'}</div>
                        </div>
                        <div className="episode-item-duration">{episode.duration || 'N/A'}</div>
                      </div>
                      
                      {/* Share button for each episode */}
                      <button 
                        className="episode-share-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          shareEpisode(
                            currentShow.title,
                            episode.title,
                            currentShow.slug,
                            episode.slug
                          );
                        }}
                        title="Share this episode"
                      >
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <circle cx="18" cy="5" r="3"></circle>
                          <circle cx="6" cy="12" r="3"></circle>
                          <circle cx="18" cy="19" r="3"></circle>
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                        </svg>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};